import { useForm } from 'react-hook-form';
import { useEffect, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form, FormField, FormItem, FormLabel, FormControl } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useUpdateFollowUp, useDeleteFollowUp } from '@/hooks/useStakeholderInteractions';
import { useLocalStorageManager } from '@/hooks/useLocalStorageManager';
import type { CRMFollowUp } from '@/lib/crm/types';

interface EditFollowUpDialogProps {
  followUp: CRMFollowUp | null;
  stakeholders?: any[];
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  stakeholder_id?: string;
  type: string;
  subject: string;
  outcome: string;
  attendees: string;
  date: string;
  notes: string;
  status: 'pending' | 'completed' | 'cancelled';
}

export function EditFollowUpDialog({ 
  followUp,
  stakeholders = [],
  isOpen, 
  onClose, 
  onSuccess
}: EditFollowUpDialogProps) {
  const { mutateAsync: updateFollowUp, isPending: isUpdating } = useUpdateFollowUp();
  const { mutateAsync: deleteFollowUp, isPending: isDeleting } = useDeleteFollowUp();
  const { getStandardValue, setStandardValue, removeStandardValue } = useLocalStorageManager();
  
  const draftKey = followUp ? `edit-followup-draft-${followUp.id}` : '';
  const hasLoadedDraftRef = useRef(false);
  
  // Find stakeholder name
  const stakeholderName = useMemo(() => {
    if (!followUp?.stakeholder_id) return 'Không xác định';
    const stakeholder = stakeholders.find(s => s.id === followUp.stakeholder_id);
    return stakeholder?.name || 'Không xác định';
  }, [followUp, stakeholders]);
  
  const form = useForm<FormData>({
    defaultValues: {
      stakeholder_id: followUp?.stakeholder_id || '',
      type: followUp?.type || 'Follow-up',
      subject: followUp?.subject || '',
      outcome: followUp?.outcome || '',
      attendees: followUp?.attendees || '',
      date: followUp?.follow_up_date ? format(new Date(followUp.follow_up_date), "yyyy-MM-dd'T'HH:mm") : '',
      notes: followUp?.notes || '',
      status: followUp?.status || 'pending'
    }
  });
  
  useEffect(() => {
    if (isOpen && followUp && draftKey && !hasLoadedDraftRef.current) {
      const draft = getStandardValue<FormData>(draftKey, null as any);
      if (draft) {
        form.reset(draft);
      } else {
        form.reset({
          stakeholder_id: followUp.stakeholder_id || '',
          type: followUp.type || 'Follow-up',
          subject: followUp.subject || followUp.notes || '',
          outcome: followUp.outcome || '',
          attendees: followUp.attendees || '',
          date: format(new Date(followUp.follow_up_date), "yyyy-MM-dd'T'HH:mm"),
          notes: followUp.notes || '',
          status: followUp.status
        });
      }
      hasLoadedDraftRef.current = true;
    }
    
    // Reset flag when dialog closes
    if (!isOpen) {
      hasLoadedDraftRef.current = false;
    }
  }, [isOpen, followUp, draftKey, getStandardValue, form]);
  
  // Auto-save draft
  useEffect(() => {
    if (!isOpen || !draftKey) return;
    const subscription = form.watch((values) => {
      setStandardValue(draftKey, values as FormData);
    });
    return () => subscription.unsubscribe();
  }, [isOpen, form, draftKey, setStandardValue]);
  
  const onSubmit = async (data: FormData) => {
    if (!followUp) return;
    
    await updateFollowUp({
      id: followUp.id,
      type: data.type,
      subject: data.subject,
      outcome: data.outcome,
      attendees: data.attendees,
      date: new Date(data.date).toISOString(),
      notes: data.notes,
      status: data.status
    });
    
    if (draftKey) removeStandardValue(draftKey);
    onSuccess();
    onClose();
  };
  
  const handleDelete = async () => {
    if (!followUp) return;
    if (!confirm('Bạn có chắc muốn xóa follow-up này?')) return;
    
    await deleteFollowUp(followUp.id);
    if (draftKey) removeStandardValue(draftKey);
    onSuccess();
    onClose();
  };
  
  const handleClose = () => {
    if (draftKey) removeStandardValue(draftKey);
    form.reset();
    onClose();
  };
  
  const isPending = isUpdating || isDeleting;
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Chỉnh sửa Follow-up</DialogTitle>
          <div className="pt-2 pb-1">
            <p className="text-sm text-muted-foreground">
              Stakeholder: <span className="font-medium text-foreground">{stakeholderName}</span>
            </p>
          </div>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              name="type"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Loại</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Follow-up">Follow-up</SelectItem>
                      <SelectItem value="Meeting">Meeting</SelectItem>
                      <SelectItem value="Call">Call</SelectItem>
                      <SelectItem value="Site Visit">Site Visit</SelectItem>
                      <SelectItem value="Delivery">Delivery</SelectItem>
                      <SelectItem value="Approval">Approval</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            
            <FormField
              name="date"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ngày/Giờ Follow-up *</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              name="subject"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Chủ đề *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Nhập chủ đề..." />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              name="outcome"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Kết quả</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Kết quả..." rows={2} />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              name="attendees"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Người tham dự</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Nguyễn Văn A, Trần Thị B..." />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              name="notes"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ghi chú</FormLabel>
                  <FormControl>
                    <Textarea 
                      {...field} 
                      placeholder="Ghi chú bổ sung..." 
                      rows={2}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              name="status"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Trạng thái</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            
            <DialogFooter className="gap-2">
              <Button 
                type="button" 
                variant="destructive" 
                onClick={handleDelete} 
                disabled={isPending}
              >
                Xóa
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleClose} 
                disabled={isPending}
              >
                Hủy
              </Button>
              <Button type="submit" disabled={isPending}>
                {isUpdating ? 'Đang lưu...' : 'Lưu'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
